// ReturnProductDrawer.jsx
import { useState, useMemo, useEffect, useRef  } from "react";
import { FaTimes, FaUndo, FaInfoCircle, FaPencilAlt, FaCheck, FaRupeeSign } from "react-icons/fa";
import Swal from "sweetalert2";
import { toast } from "react-toastify";
import { httpService } from "../../services/httpService";

const RETURN_REASONS = [
  "Expired",
  "Damaged / broken",
  "Customer changed mind",
  "Wrong item",
  "Other",
];

const MODES = {
  PRODUCT_RETURN: "service_product_return",
  PARTIAL_REFUND: "partial_refund",
};

const ReturnProductDrawer = ({
  isOpen,
  onClose,
  orderId,
  orderNumber,
  clientId,
  products = [],
  balanceDue = 0,      // paid amount so far — caps the refund
  isOrderClosed = false, // NEW — controls whether "Return Products" tab is available at all
  onReturnSuccess,
}) => {
  // Product return is only ever allowed on a closed order with returnable products.
  //const canReturnProducts = isOrderClosed && products.length > 0;
  const paidAmount = Number(balanceDue) || 0;
  const canRefund = paidAmount > 0;
  const canReturnProducts = isOrderClosed && products.length > 0;

  // Tabs only make sense when BOTH options are available
  const showTabs = canReturnProducts && canRefund;

  const title =
    canReturnProducts && canRefund
      ? "Return / Refund"
      : canReturnProducts
      ? "Return Products"
      : "Refund";

  const noun = canRefund ? "refund" : "return";

  const [mode, setMode] = useState(
    canReturnProducts ? MODES.PRODUCT_RETURN : MODES.PARTIAL_REFUND,
  );
  
  useEffect(() => {
    if (!isOpen) return;
    setMode(canReturnProducts ? MODES.PRODUCT_RETURN : MODES.PARTIAL_REFUND);
  }, [isOpen, canReturnProducts]);

  const [submitting, setSubmitting] = useState(false);
  
  const idemRef = useRef({ fp: null, key: null });

  // Same payload -> same key (safe retry). Changed payload -> new key.
  const getIdempotencyKey = (payload) => {
    const fp = JSON.stringify(payload);
    if (idemRef.current.fp !== fp) {
      idemRef.current = { fp, key: crypto.randomUUID() };
    }
    return idemRef.current.key;
  };
  // ---------- Product-return state ----------
  const [selection, setSelection] = useState({});
  const getRow = (key) =>
    selection[key] || {
      checked: false,
      quantity: 1,
      reason: RETURN_REASONS[0],
      note: "",
      collapsed: false,
    };
  const updateRow = (key, patch) =>
    setSelection((prev) => ({ ...prev, [key]: { ...getRow(key), ...patch } }));

  const toggleChecked = (product) => {
    const row = getRow(product.key);
    updateRow(product.key, {
      checked: !row.checked,
      quantity: row.quantity || 1,
      reason: row.reason || RETURN_REASONS[0],
      collapsed: false,
    });
  };
  const collapseRow = (key) => updateRow(key, { collapsed: true });
  const expandRow = (key) => updateRow(key, { collapsed: false });

  const selectedItems = useMemo(() => {
    return products
      .filter((p) => getRow(p.key).checked)
      .map((p) => {
        const row = getRow(p.key);
        //const qty = Math.min(Math.max(Number(row.quantity) || 0, 0), p.purchasedQty);
        const qty = Math.min(Math.max(Number(row.quantity) || 0, 0), p.returnableQty);
        return {
          ...p,
          quantity: qty,
          reason: row.reason,
          note: row.note,
          refundAmount: Number((qty * p.unitPrice).toFixed(2)),
        };
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [products, selection]);

  const productRefundTotal = useMemo(
    () => selectedItems.reduce((sum, i) => sum + i.refundAmount, 0),
    [selectedItems],
  );

  const handleQuantityChange = (product, value) => {
   // const qty = Math.min(Math.max(Number(value) || 0, 0), product.purchasedQty);
    const qty = Math.min(Math.max(Number(value) || 0, 0), product.returnableQty);
   updateRow(product.key, { quantity: qty });
  };

  // ---------- Partial-refund state ----------
  const [refundAmount, setRefundAmount] = useState("");
  const [refundNote, setRefundNote] = useState("");
  
  // NEW: compare in paise to avoid floating-point issues
  const toPaise = (n) => Math.round(Number(n || 0) * 100);

  // NEW: recalculates on every keystroke
  const refundAmountError = useMemo(() => {
    if (refundAmount === "") return "";
    const amt = Number(refundAmount);
    if (!Number.isFinite(amt) || amt <= 0) return "Enter an amount greater than 0";
    if (toPaise(amt) > toPaise(balanceDue)) {
      return `Amount exceeds the refundable balance of ₹${Number(balanceDue).toFixed(2)}`;
    }
    return "";
  }, [refundAmount, balanceDue]);

  // ---------- Shared ----------
  const handleClose = () => {
    setSelection({});
    setRefundAmount("");
    setRefundNote("");
    onClose?.();
  };

  const validateProductReturn = () => {
    if (selectedItems.length === 0) {
      toast.error("Select at least one product to return");
      return false;
    }
    for (const item of selectedItems) {
      if (!item.quantity || item.quantity <= 0) {
        toast.error(`Enter a valid return quantity for ${item.name}`);
        return false;
      }
      if (!item.reason) {
        toast.error(`Select a reason for ${item.name}`);
        return false;
      }
    }
    return true;
  };

  const validatePartialRefund = () => {
    const amt = Number(refundAmount);
    if (!amt || amt <= 0) {
      toast.error("Enter a valid refund amount");
      return false;
    }
    // if (balanceDue > 0 && amt > balanceDue) {
    //   toast.error(`Refund amount can't exceed the paid amount (₹${balanceDue.toFixed(0)})`);
    //   return false;
    // }
    if (toPaise(amt) > toPaise(balanceDue)) {
      toast.error("Refund amount exceeds the refundable balance");
      return false;
    }
    if (!refundNote.trim()) {
      toast.error("Add a short note explaining the refund");
      return false;
    }
    return true;
  };

  const buildProductReturnPayload = () => ({
    order_id: orderId,
    order_category: "refund",
    splitCase: MODES.PRODUCT_RETURN,
    products: selectedItems.map((i) => ({
      // copy the whole original product object, then override qty + service_ref_id
      ...i.raw,
      quantity: i.quantity,
      service_ref_id: i.serviceRefId,
      product_id: i.productId,
      price: i.unitPrice,
      reason: i.reason,
      note: i.note || "",
    })),
    refundAmount: Number(productRefundTotal.toFixed(2)),
    notes: selectedItems.map((i) => i.note).filter(Boolean).join("; ") || "Product return",
  });

  // Order-level refund — no service reference needed, matches the API contract exactly.
  const buildPartialRefundPayload = () => ({
    order_id: orderId,
    order_category: "refund",
    splitCase: MODES.PARTIAL_REFUND,
    returnAmount: Number(Number(refundAmount).toFixed(2)),
    notes: refundNote.trim(),
  });

  const handleConfirm = async () => {
    const isProductMode = mode === MODES.PRODUCT_RETURN;
    if (isProductMode ? !validateProductReturn() : !validatePartialRefund()) return;

    const amount = isProductMode ? productRefundTotal : Number(refundAmount);
    const count = isProductMode ? selectedItems.length : null;

    const result = await Swal.fire({
      // $title: "Confirm refund?",
      // text: isProductMode
      //   ? `This will refund ₹${amount.toFixed(2)} for ${count} item(s). This cannot be undone.`
      //   : `This will refund ₹{amount.toFixed(2)} to the customer. This cannot be undone.`,
      title: `Confirm ${noun}?`,
      text: isProductMode
        ? `This will ${canRefund ? "refund" : "record a return of"} ₹${amount.toFixed(0)} for ${count} item(s). This cannot be undone.`
        : `This will refund ₹${amount.toFixed(0)} to the customer. This cannot be undone.`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#e11d48",
      cancelButtonColor: "#3085d6",
      // confirmButtonText: "Yes, refund",
      confirmButtonText: `Yes, ${noun}`,
      cancelButtonText: "No",
    });
    if (!result.isConfirmed) return;

    //const payload = isProductMode ? buildProductReturnPayload() : buildPartialRefundPayload();
     const basePayload = isProductMode ? buildProductReturnPayload() : buildPartialRefundPayload();
     const payload = { ...basePayload, idempotencyKey: getIdempotencyKey(basePayload) };
    setSubmitting(true);
    try {
      const res = await httpService.post("/vendororder/splitOrder", payload);
      if (res.code === 200 || res.status) {
        idemRef.current = { fp: null, key: null };
        toast.success(
          isProductMode ? "Product return recorded successfully." : "Refund recorded successfully.",
        );
        setSelection({});
        setRefundAmount("");
        setRefundNote("");
        onReturnSuccess?.(res.data);
      } else {
        throw new Error(res.message || "Failed to record refund");
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || "Failed to record refund");
    } finally {
      setSubmitting(false);
    }
  };

  if (!isOpen) return null;

  // const confirmDisabled =
  //   submitting ||
  //   (mode === MODES.PRODUCT_RETURN ? selectedItems.length === 0 : !refundAmount);
  const confirmDisabled =
    submitting ||
    (mode === MODES.PRODUCT_RETURN
      ? selectedItems.length === 0
      : !refundAmount || !!refundAmountError);
  const confirmTotal = mode === MODES.PRODUCT_RETURN ? productRefundTotal : Number(refundAmount || 0);

  return (
    <>
      <div
        className="fixed inset-0 bg-black/40 z-[60]"
        style={{ backdropFilter: "blur(4px)", WebkitBackdropFilter: "blur(4px)" }}
        onClick={handleClose}
      />
      <div className="fixed right-0 top-0 h-full z-[70] flex flex-col bg-white shadow-2xl w-3/4 sm:w-[520px]">
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200">
          <span className="font-bold text-base flex items-center gap-2 text-gray-800 min-w-0">
            <FaUndo className="text-rose-600 shrink-0" />
            {/* <span className="truncate">{canReturnProducts ? "Return / Refund" : "Refund"}</span> */}
            <span className="truncate">{title}</span>
            {orderNumber && (
              <span className="text-xs font-normal text-gray-500 whitespace-nowrap">
                #{orderNumber}
              </span>
            )}
          </span>
          <button
            onClick={handleClose}
            className="w-8 h-8 flex items-center justify-center rounded-full bg-gray-100 hover:bg-gray-200 transition text-gray-500 shrink-0"
          >
            <FaTimes />
          </button>
        </div>

        {/* Mode tabs — only shown when Product Return is actually available (closed order + returnable products) */}
        {/* {canReturnProducts && ( */}
        {showTabs && (
          <div className="flex gap-2 px-4 pt-3">
            <button
              onClick={() => setMode(MODES.PRODUCT_RETURN)}
              className={`flex-1 text-xs font-semibold py-2 rounded-lg border transition ${
                mode === MODES.PRODUCT_RETURN
                  ? "bg-rose-600 text-white border-rose-600"
                  : "bg-white text-gray-600 border-gray-300 hover:bg-gray-50"
              }`}
            >
              Return Products
            </button>
            <button
              onClick={() => setMode(MODES.PARTIAL_REFUND)}
              className={`flex-1 text-xs font-semibold py-2 rounded-lg border transition ${
                mode === MODES.PARTIAL_REFUND
                  ? "bg-rose-600 text-white border-rose-600"
                  : "bg-white text-gray-600 border-gray-300 hover:bg-gray-50"
              }`}
            >
              Refund
            </button>
          </div>
        )}

        <div className="flex-1 overflow-y-auto px-4 py-3">
          {mode === MODES.PRODUCT_RETURN ? (
            products.length === 0 ? (
              <p className="text-sm text-gray-500 text-center py-8">
                No returnable products on this order.
              </p>
            ) : (
              products.map((product) => {
                const row = getRow(product.key);
                const isCollapsedSelected = row.checked && row.collapsed;

                if (isCollapsedSelected) {
                  return (
                    <div
                      key={product.key}
                      className="border border-rose-300 bg-rose-50/40 rounded-xl px-3 py-2 mb-2"
                    >
                      <div className="flex items-center justify-between gap-2">
                        <label className="flex items-center gap-2 flex-1 min-w-0 cursor-pointer">
                          <input
                            type="checkbox"
                            checked
                            onChange={() => toggleChecked(product)}
                            className="w-4 h-4 rounded border-gray-300 text-rose-600 focus:ring-rose-500 shrink-0"
                          />
                          <span className="font-medium text-sm text-gray-800 truncate">
                            {product.name}
                          </span>
                        </label>
                        <span className="text-xs text-gray-500 whitespace-nowrap">
                          qty {row.quantity} · {row.reason}
                        </span>
                        <span className="text-sm font-medium text-gray-800 whitespace-nowrap">
                          ₹{(row.quantity * product.unitPrice).toFixed(2)}
                        </span>
                        <button
                          onClick={() => expandRow(product.key)}
                          className="text-rose-600 hover:text-rose-700 shrink-0"
                          title="Edit"
                        >
                          <FaPencilAlt size={13} />
                        </button>
                      </div>
                      <div className="text-[11px] text-gray-400 mt-1 ml-6 truncate">
                        {product.petName ? `${product.petName} · ` : ""}
                        {product.serviceName}
                      </div>
                    </div>
                  );
                }

                return (
                  <div
                    key={product.key}
                    className={`border rounded-xl p-3 mb-2 transition ${
                      row.checked ? "border-rose-300 bg-rose-50/40" : "border-gray-200"
                    }`}
                  >
                    <label className="flex items-start gap-3 cursor-pointer">
                      <input
                        type="checkbox"
                        className="mt-1 w-4 h-4 rounded border-gray-300 text-rose-600 focus:ring-rose-500"
                        checked={row.checked}
                        onChange={() => toggleChecked(product)}
                      />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between gap-2">
                          <span className="font-medium text-sm text-gray-800 truncate">
                            {product.name}
                          </span>
                          <span className="text-sm text-gray-700 whitespace-nowrap">
                            ₹{Number(product.unitPrice).toFixed(2)}
                          </span>
                        </div>
                        <div className="text-xs text-gray-500 mt-0.5">
                          {product.petName ? `${product.petName} · ` : ""}
                          {/* {product.serviceName} · purchased qty {product.purchasedQty} */}
                          {product.serviceName} · purchased {product.purchasedQty}
                          {product.returnedQty > 0 && ` · ${product.returnedQty} returned`}
                          {` · ${product.returnableQty} available to return`}
                        </div>

                        {row.checked && (
                          <div className="mt-2 space-y-2" onClick={(e) => e.stopPropagation()}>
                            <div className="grid grid-cols-2 gap-2">
                              <div>
                                <label className="text-[11px] text-gray-400">Return qty</label>
                                <input
                                  type="number"
                                  min={1}
                                  //max={product.purchasedQty}
                                  max={product.returnableQty}
                                  value={row.quantity}
                                  onChange={(e) => handleQuantityChange(product, e.target.value)}
                                  className="w-full border border-gray-300 rounded-lg px-2 py-1 text-sm"
                                />
                              </div>
                              <div>
                                <label className="text-[11px] text-gray-400">Refund amount</label>
                                <input
                                  type="text"
                                  readOnly
                                  value={`₹${(
                                    (Number(row.quantity) || 0) * product.unitPrice
                                  ).toFixed(2)}`}
                                  className="w-full border border-gray-200 bg-gray-50 rounded-lg px-2 py-1 text-sm text-gray-600"
                                />
                              </div>
                            </div>

                            <div>
                              <label className="text-[11px] text-gray-400">Reason</label>
                              <select
                                value={row.reason}
                                onChange={(e) => updateRow(product.key, { reason: e.target.value })}
                                className="w-full border border-gray-300 rounded-lg px-2 py-1 text-sm"
                              >
                                {RETURN_REASONS.map((r) => (
                                  <option key={r} value={r}>
                                    {r}
                                  </option>
                                ))}
                              </select>
                            </div>

                            <div>
                              <label className="text-[11px] text-gray-400">Note (optional)</label>
                              <input
                                type="text"
                                placeholder="Add a short note"
                                value={row.note}
                                onChange={(e) => updateRow(product.key, { note: e.target.value })}
                                className="w-full border border-gray-300 rounded-lg px-2 py-1 text-sm"
                              />
                            </div>

                            <div className="flex justify-end pt-1">
                              <button
                                onClick={() => collapseRow(product.key)}
                                disabled={!row.quantity || !row.reason}
                                className={`text-xs font-semibold px-3 py-1.5 rounded-lg border flex items-center gap-1 ${
                                  !row.quantity || !row.reason
                                    ? "border-gray-200 text-gray-300 cursor-not-allowed"
                                    : "border-rose-300 text-rose-700 hover:bg-rose-100"
                                }`}
                              >
                                <FaCheck size={11} /> Done
                              </button>
                            </div>
                          </div>
                        )}
                      </div>
                    </label>
                  </div>
                );
              })
            )
          ) : (
            <div className="space-y-3">
              <div>
                <label className="text-[11px] text-gray-400">Refund amount</label>
                <div className="relative">
                  <FaRupeeSign className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-xs" />
                  <input
                    type="number"
                    min={0}
                    step="0.01"
                    placeholder="0.00"
                    value={refundAmount}
                    onChange={(e) => setRefundAmount(e.target.value)}
                    className={`w-full border rounded-lg pl-7 pr-2 py-2 text-sm ${
                      refundAmountError
                        ? "border-red-500 focus:outline-none focus:ring-1 focus:ring-red-500"
                        : "border-gray-300"
                    }`}
                  />
                </div>
                {/* {balanceDue > 0 && (
                  <p className="text-[11px] text-gray-400 mt-1">
                    Up to ₹{balanceDue.toFixed(0)} paid on this order
                  </p>
                )} */}
                {refundAmountError ? (
                  <p className="text-[11px] text-red-600 mt-1">{refundAmountError}</p>
                ) : (
                  <p className="text-[11px] text-gray-400 mt-1 flex justify-between">
                    <span>Refundable balance: ₹{Number(balanceDue).toFixed(2)}</span>
                    <button
                      type="button"
                      onClick={() => setRefundAmount(Number(balanceDue).toFixed(2))}
                      className="text-rose-600 font-medium hover:underline"
                    >
                      Refund full amount
                    </button>
                  </p>
                )}
              </div>
              <div>
                <label className="text-[11px] text-gray-400">Reason / note</label>
                <textarea
                  rows={3}
                  placeholder="e.g. Customer dissatisfied with the service"
                  value={refundNote}
                  onChange={(e) => setRefundNote(e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-2 py-2 text-sm resize-none"
                />
              </div>
            </div>
          )}
        </div>

        <div className="border-t border-gray-200 px-4 py-3">
          <div className="flex justify-between text-sm font-bold text-gray-800 mb-2">
            {/* <span>Total refund</span> */}
            <span>{canRefund ? "Total refund" : "Return value"}</span>
            <span>₹{confirmTotal.toFixed(2)}</span>
          </div>
          <p className="text-[11px] text-gray-500 flex items-center gap-1 mb-3">
            <FaInfoCircle className="text-gray-400" />
            {mode === MODES.PRODUCT_RETURN
              ? "Creates a return record and a credit note. The original invoice stays unchanged."
              : "Creates a refund record against this order. The original invoice stays unchanged."}
          </p>
          <div className="flex gap-2">
            <button
              onClick={handleClose}
              className="flex-1 border border-gray-300 bg-white py-2 rounded-xl font-semibold text-sm hover:bg-gray-50 transition"
            >
              Cancel
            </button>
            <button
              onClick={handleConfirm}
              disabled={confirmDisabled}
              className={`flex-1 bg-rose-600 text-white py-2 rounded-xl font-semibold text-sm hover:bg-rose-700 transition ${
                confirmDisabled ? "opacity-50 cursor-not-allowed" : ""
              }`}
            >
              {/* {submitting ? "Processing..." : "Confirm refund"} */}
              {submitting ? "Processing..." : `Confirm ${noun}`}
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default ReturnProductDrawer;